import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Modal, Breadcrumb, Typography, Spin, Select } from 'antd';
import { Country } from 'country-state-city';
import { formItemLayout, unexpectedError, tailFormItemLayout, handleBackendFormErrors, backButton } from '../../common_functions';

const Collection = props => {
  const { section, id } = useParams();
  const [collection, setCollection] = useState({});
  const [loading, setLoading] = useState(false);
  const [collType, setCollType] = useState([]);

  const contactCode = (
    <Form.Item name="contact_phone_code" initialValue={collection.phone_code || 'US-1'} noStyle>
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) => option.children.includes(input)}
        style={{
          width: 100,
        }}
      >
        {Country.getAllCountries().map(item => {
          return (
            <Option key={item.isoCode} value={item.isoCode + '-' + item.phonecode}>
              {item.flag} {item.phonecode}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );
  const altPhoneCode = (
    <Form.Item name="alt_phone_code" initialValue={collection.phone_code || 'US-1'} noStyle>
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) => option.children.includes(input)}
        style={{
          width: 100,
        }}
      >
        {Country.getAllCountries().map(item => {
          return (
            <Option key={item.isoCode} value={item.isoCode + '-' + item.phonecode}>
              {item.flag} {item.phonecode}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  const formRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        let { data: { meta: { options: { choices: type = [] } = {} } = {} } = {} } = await props.fetchFieldValues('collections', {
          fields: ['associated_to'],
        });
        setCollType(type);
        if (section == 'edit' && id) {
          let { data } = await props.getItem('collections', id);
          formRef.current.setFieldsValue(data);
          setCollection(data);
        }
      } catch (error) {
        console.error(error);
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    try {
      setLoading(true);
      let newState = { ...collection, ...values };
      const response = await props.saveItem('collections', newState);

      if (response) {
        notification['success']({
          message: 'Collection saved successfully!',
          duration: 2,
        });
        backButton(props, `/collections`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  let validateMobileNumber = (rule, value) => {
    const reg = /^-?(0|[0-9]*)(\.[0-9]*)?$/;
    let field = 'Alt contact phone';
    if (rule.field === 'contact_phone') {
      field = 'Contact phone';
    }
    if (!!value && !reg.test(value)) {
      return Promise.reject(`Enter digits only!`);
    }
    if (!!value && value.length < 10) {
      return Promise.reject(`${field} not less than 10 digits!`);
    }
    return Promise.resolve();
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Collections", href: "/collections" }, { title: id ? `Edit Collection` : 'Add Collection' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id ? `Edit Collection: ${id}` : 'Add Collection'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              {id ? (
                <Row gutter={24}>
                  {' '}
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item name="id" label="Collection Code(System Generated)">
                      <Input placeholder="Enter collection code" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item
                      name="associated_to"
                      label="Associated to"
                      rules={[
                        {
                          required: true,
                          message: 'Associated to is required!',
                        },
                      ]}
                    >
                      <Select placeholder="Select associated to" disabled={section == 'edit' && id ? true : false}>
                        {collType.map(item => {
                          return <Option key={item.value} value={item.value}>{item.text}</Option>;
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Row gutter={24}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item
                      name="associated_to"
                      label="Associated to"
                      rules={[
                        {
                          required: true,
                          message: 'Associated to is required!',
                        },
                      ]}
                    >
                      <Select placeholder="Select associated to">
                        {collType.map(item => {
                          return <Option key={item.value} value={item.value}>{item.text}</Option>;
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="name"
                    label="Collection Name"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Collection name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter collection name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="address"
                    label="Collection Address"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Collection address is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter collection address" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="collection_designation" label="Collection Designation">
                    <Input placeholder="Enter collection designation" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="location_cona_code" label="Collection Location Cona Code">
                    <Input placeholder="Enter collection location cona code" style={{ textTransform: 'uppercase' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="contact_name"
                    label="Contact Name/Title"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Contact name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter contact name" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="alt_contact_name" label="Alt Contact Name/Title">
                    <Input placeholder="Enter alt contact name" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="contact_phone"
                    label="Contact Phone"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Contact phone ie required',
                      },
                      {
                        validator: validateMobileNumber,
                      },
                    ]}
                  >
                    <Input type="phone" addonBefore={contactCode} placeholder="Enter contact phone" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="alt_contact_phone"
                    label="Alt Contact Phone"
                    rules={[
                      {
                        whitespace: true,
                        validator: validateMobileNumber,
                      },
                    ]}
                  >
                    <Input type="phone" addonBefore={altPhoneCode} placeholder="Enter alt contact phone" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="contact_email"
                    label="Contact Email"
                    rules={[
                      {
                        required: true,
                        message: 'Contact email is required!',
                      },
                      {
                        type: 'email',
                        message: 'Enter valid email!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter contact name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="alt_contact_email"
                    label="Alt Contact Email"
                    rules={[
                      {
                        whitespace: true,
                        type: 'email',
                        message: 'Enter valid email!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter alt contact name" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="description" label="Collection Description">
                    <Input.TextArea placeholder="Collection description" autosize={{ minRows: 3 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/collections')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(
                  props.roleType,
                  props.tabs_permissions.collections,
                  !!collection ? collection.id : '',
                  props.collections_permissions.edit
                )}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default Collection;
